import { useEffect, useState } from 'react';
import { collection, query, limit, onSnapshot, doc, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createPortalUser } from '../../utils/createUser';
import {
  FOOTBALL_OPS_PERMISSIONS, DEFAULT_FOOTBALL_OPS_PERMISSIONS, withFootballOpsDefaults,
} from '../../utils/footballOpsPermissions';

const ALL_ROLES = ['admin', 'staff', 'customer', 'supplier', 'reseller', 'tour_leader', 'football_ops', 'marketing'];
const INVITE_ROLES = ['staff', 'customer', 'supplier', 'tour_leader', 'football_ops', 'marketing'];

const ROLE_STYLES = {
  admin:       'bg-[#F5F3FF] text-[#5B21B6]',
  staff:       'bg-blue-light text-pb',
  customer:    'bg-green-light text-[#065F46]',
  supplier:    'bg-[#CCFBF1] text-[#0F766E]',
  reseller:    'bg-amber-light text-[#92400E]',
  tour_leader: 'bg-[#FFF7ED] text-[#C2410C]',
  football_ops: 'bg-green-light text-sg',
  marketing:   'bg-[#FCE7F3] text-[#9D174D]',
};

const ROLE_LABELS = {
  admin: 'Admin', staff: 'Staff', customer: 'Customer',
  supplier: 'Supplier', reseller: 'Reseller', tour_leader: 'Tour Leader',
  football_ops: 'Football Operations', marketing: 'Marketing',
};

// Portal access. Staff, tour leaders and football operations are each granted
// one or more of these (staff use them for the whole portal; tour leaders for
// their database access; football operations for which tours they can see);
// other roles don't use them.
const PORTAL_OPTIONS = [
  { key: 'uk_to_spain', label: 'UK to Spain' },
  { key: 'spain_to_uk', label: 'Spain to UK' },
  { key: 'padelante',   label: 'Padelante' },
];
const PORTAL_LABELS = Object.fromEntries(PORTAL_OPTIONS.map((p) => [p.key, p.label]));
const PORTAL_ROLES = ['staff', 'tour_leader', 'football_ops'];
const carriesPortals = (roles) => (roles || []).some((r) => PORTAL_ROLES.includes(r));

const emptyForm = {
  email: '', name: '', role: 'customer', portals: ['uk_to_spain'],
  footballOpsPermissions: { ...DEFAULT_FOOTBALL_OPS_PERMISSIONS },
};

// What a Football Operations user can see — admins tick these per user.
function FootballOpsAccessEditor({ value, onChange }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.6px] mb-1.5">Football Ops access</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {FOOTBALL_OPS_PERMISSIONS.map((p) => (
          <label key={p.key} className="flex items-start gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={value[p.key] !== false}
              onChange={() => onChange({ ...value, [p.key]: value[p.key] === false })}
              className="mt-[2px] rounded border-[#D1D5DB] text-pb focus:ring-pb"
            />
            <span className="text-[12px] text-[#374151] leading-snug">
              {p.label}{!p.enforced && <span className="text-[#9CA3AF]"> *</span>}
              {p.hint && <span className="block text-[11px] text-[#9CA3AF]">{p.hint}</span>}
            </span>
          </label>
        ))}
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-1.5 leading-snug">
        Only applies to people whose sole role is Football Operations. * Hidden on screen only;
        the rest are also blocked in the database. Changes apply next time they open the portal.
      </p>
    </div>
  );
}

const isFootballOpsLimited = (u) => Object.values(withFootballOpsDefaults(u.footballOpsPermissions)).some((v) => !v);

// The portals a user can access — explicit `portals` field, else derived from
// legacy per-portal roles (mirrors AuthContext).
function getUserPortals(u) {
  if (Array.isArray(u.portals)) return u.portals;
  const roles = Array.isArray(u.roles) ? u.roles : u.role ? [u.role] : [];
  const out = [];
  if (roles.includes('staff')) out.push('uk_to_spain');
  if (roles.includes('staff_es')) out.push('spain_to_uk');
  if (roles.includes('staff_pad')) out.push('padelante');
  return out;
}

function formatDate(timestamp) {
  if (!timestamp?.seconds) return '—';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

// Returns the roles array regardless of whether the doc uses legacy role: string or roles: string[]
function getUserRoles(u) {
  if (Array.isArray(u.roles)) return u.roles;
  if (u.role) return [u.role];
  return [];
}

export default function Users() {
  const { currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successEmail, setSuccessEmail] = useState('');
  const [changingRoleFor, setChangingRoleFor] = useState(null);
  const [roleChangeValues, setRoleChangeValues] = useState([]);
  const [portalChangeValues, setPortalChangeValues] = useState([]);
  const [foPermsChangeValues, setFoPermsChangeValues] = useState(DEFAULT_FOOTBALL_OPS_PERMISSIONS);
  const [roleChangeSaving, setRoleChangeSaving] = useState(false);
  const [resendingInviteFor, setResendingInviteFor] = useState(null);
  const [resendSuccess, setResendSuccess] = useState('');
  const [inviteWarning, setInviteWarning] = useState('');
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null); // { id, email }
  const [deletingUser, setDeletingUser] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'users'), limit(500)), (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setUsers(docs);
      setLoading(false);
    });
    return unsub;
  }, []);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleInvite(e) {
    e.preventDefault();
    setError('');
    setSuccessEmail('');

    if (!form.email.trim()) { setError('Email is required.'); return; }
    if (!form.role)          { setError('Please select a role.'); return; }
    if (form.role === 'staff' && form.portals.length === 0) {
      setError('Select at least one portal for this staff member.'); return;
    }
    if (form.role === 'tour_leader' && form.portals.length === 0) {
      setError('Select at least one portal for this tour leader.'); return;
    }
    if (form.role === 'football_ops' && form.portals.length === 0) {
      setError('Select at least one portal for this football operations user.'); return;
    }

    const already = users.find(
      (u) => u.email?.toLowerCase() === form.email.trim().toLowerCase()
    );
    if (already) { setError('A user with that email already exists.'); return; }

    setSaving(true);
    try {
      const { inviteSent } = await createPortalUser({
        email: form.email,
        role: form.role,
        name: form.name,
        createdByEmail: currentUser.email,
        portals: carriesPortals([form.role]) ? form.portals : undefined,
        footballOpsPermissions: form.role === 'football_ops' && isAdmin ? form.footballOpsPermissions : undefined,
      });
      setSuccessEmail(form.email.trim());
      if (!inviteSent) setInviteWarning(form.email.trim());
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('That email is already registered. Use the manage roles button to update their permissions.');
      } else {
        setError('Failed to create user. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  function toggleFormPortal(key) {
    setForm((prev) => ({
      ...prev,
      portals: prev.portals.includes(key) ? prev.portals.filter((p) => p !== key) : [...prev.portals, key],
    }));
  }

  function startRoleChange(user) {
    setChangingRoleFor(user.id);
    setRoleChangeValues(getUserRoles(user));
    setPortalChangeValues(getUserPortals(user));
    setFoPermsChangeValues(withFootballOpsDefaults(user.footballOpsPermissions));
  }

  function toggleRole(role) {
    setRoleChangeValues((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }

  function togglePortalChange(key) {
    setPortalChangeValues((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  }

  async function saveRoleChange(userId) {
    if (roleChangeValues.length === 0) return;
    const needsPortals = carriesPortals(roleChangeValues);
    if (needsPortals && portalChangeValues.length === 0) return; // staff/tour leaders need ≥1 portal
    setRoleChangeSaving(true);
    try {
      await updateDoc(doc(db, 'users', userId), {
        roles: roleChangeValues,
        role: roleChangeValues[0],
        // Only staff / tour leaders carry portal access; clear it otherwise.
        portals: needsPortals ? portalChangeValues : [],
        ...(roleChangeValues.includes('football_ops') ? { footballOpsPermissions: foPermsChangeValues } : {}),
      });
      setChangingRoleFor(null);
    } catch (err) {
      console.error('Role change failed:', err);
    } finally {
      setRoleChangeSaving(false);
    }
  }

  async function resendInvite(user) {
    setResendingInviteFor(user.id);
    setResendSuccess('');
    try {
      await updateDoc(doc(db, 'users', user.id), { resendInviteAt: serverTimestamp() });
      setResendSuccess(user.email);
    } catch (err) {
      console.error('Resend invite failed:', err);
      alert(`Failed to resend invite to ${user.email}.`);
    } finally {
      setResendingInviteFor(null);
    }
  }

  async function handleDeleteUser() {
    if (!confirmDeleteUser) return;
    setDeletingUser(true);
    try {
      await addDoc(collection(db, 'userDeleteRequests'), {
        uid: confirmDeleteUser.id,
        requestedByUid: currentUser.uid,
        requestedAt: serverTimestamp(),
        status: 'pending',
      });
      setConfirmDeleteUser(null);
    } catch (err) {
      console.error('Delete user failed:', err);
      alert(`Failed to delete user: ${err.message}`);
    } finally {
      setDeletingUser(false);
    }
  }

  const displayRoles = ['admin', 'staff', 'customer', 'supplier'];
  const counts = displayRoles.reduce((acc, r) => {
    acc[r] = users.filter((u) => getUserRoles(u).includes(r)).length;
    return acc;
  }, {});

  return (
    <>
      {confirmDeleteUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-[12px] shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-[16px] font-bold text-[#111827] mb-2">Delete user?</h3>
            <p className="text-[13px] text-[#6B7280] mb-5">
              <strong>{confirmDeleteUser.email}</strong> will be permanently removed from the portal and will no longer be able to log in. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteUser}
                disabled={deletingUser}
                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-dr hover:bg-[#7F1D1D] rounded-[8px] transition disabled:opacity-60"
              >
                {deletingUser ? 'Deleting…' : 'Delete permanently'}
              </button>
              <button
                onClick={() => setConfirmDeleteUser(null)}
                disabled={deletingUser}
                className="px-[18px] py-[9px] text-[13px] text-[#6B7280] hover:text-[#374151] transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Users</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">Invite and manage portal access for staff, customers, and suppliers.</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setSuccessEmail(''); }}
            className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-[18px] py-[9px] rounded-[8px] transition"
          >
            + Invite user
          </button>
        )}
      </div>

      {/* Success banners */}
      {successEmail && (
        <div className="mb-6 bg-green-light border border-sg/25 text-sg text-[13px] rounded-[8px] px-4 py-3 flex items-center justify-between">
          <span>Invite sent to <strong>{successEmail}</strong> — they'll receive an email to set their password.</span>
          <button onClick={() => setSuccessEmail('')} className="ml-4 font-medium hover:opacity-70 transition">Dismiss</button>
        </div>
      )}
      {resendSuccess && (
        <div className="mb-6 bg-green-light border border-sg/25 text-sg text-[13px] rounded-[8px] px-4 py-3 flex items-center justify-between">
          <span>Invite resent to <strong>{resendSuccess}</strong>.</span>
          <button onClick={() => setResendSuccess('')} className="ml-4 font-medium hover:opacity-70 transition">Dismiss</button>
        </div>
      )}
      {inviteWarning && (
        <div className="mb-6 bg-amber-light border border-ba/25 text-[#92400E] text-[13px] rounded-[8px] px-4 py-3 flex items-center justify-between">
          <span>User <strong>{inviteWarning}</strong> was created but the invite email failed to send. Use <strong>Resend invite</strong> to send it manually.</span>
          <button onClick={() => setInviteWarning('')} className="ml-4 font-medium hover:opacity-70 transition">Dismiss</button>
        </div>
      )}

      {/* Invite form */}
      {showForm && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 mb-6">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-5">Invite New User</h3>
          <form onSubmit={handleInvite} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">
                  Email address <span className="text-dr">*</span>
                </label>
                <input
                  type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="user@example.com" autoFocus
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Full name</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">
                  Role <span className="text-dr">*</span>
                </label>
                <select
                  name="role" value={form.role} onChange={handleChange}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  {INVITE_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
            </div>
            {carriesPortals([form.role]) && (
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-2">
                  Portal access <span className="text-dr">*</span>
                </label>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {PORTAL_OPTIONS.map((p) => (
                    <label key={p.key} className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={form.portals.includes(p.key)}
                        onChange={() => toggleFormPortal(p.key)}
                        className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                      />
                      <span className="text-[13px] text-[#374151]">{p.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-[#9CA3AF] mt-1.5">
                  {form.role === 'tour_leader'
                    ? 'Tick every portal whose databases this tour leader should be able to view and manage.'
                    : form.role === 'football_ops'
                      ? "Tick every portal whose tours they should see. They get a read-only view of each tour's team info, fixtures, teams and itineraries."
                      : 'Tick every portal this staff member should be able to access. They can switch between them once logged in.'}
                </p>
              </div>
            )}
            {form.role === 'football_ops' && isAdmin && (
              <FootballOpsAccessEditor
                value={form.footballOpsPermissions}
                onChange={(v) => setForm((prev) => ({ ...prev, footballOpsPermissions: v }))}
              />
            )}
            <div className="bg-blue-light border border-pb/20 rounded-[8px] px-4 py-3 text-[12px] text-pb">
              The user will receive an email from Firebase with a link to set their password.
              Once set, they can log in at <span className="font-medium">msttoursportal.com</span>.
              Roles and portal access can be changed later from the users list.
            </div>
            {error && <p className="text-[13px] text-dr">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit" disabled={saving}
                className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
              >
                {saving ? 'Sending invite…' : 'Send invite'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}
                className="px-[18px] py-[9px] text-[13px] text-[#6B7280] hover:text-[#374151] transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Role summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {displayRoles.map((r) => (
          <div key={r} className="bg-white rounded-[10px] border border-[#E5E7EB] px-[18px] py-[16px] flex items-center gap-3">
            <span className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${ROLE_STYLES[r]}`}>
              {ROLE_LABELS[r]}
            </span>
            <span className="text-[26px] font-bold text-[#111827]">{counts[r] || 0}</span>
          </div>
        ))}
      </div>

      {/* Users table */}
      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-10 text-center">
          <p className="text-[13px] text-[#9CA3AF]">No users yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface border-b border-[#E5E7EB]">
                <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Name / Email</th>
                <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Roles</th>
                <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Added by</th>
                <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Date added</th>
                {isAdmin && <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const roles = getUserRoles(u);
                return (
                  <tr key={u.id} className="border-b border-[#F3F4F6] hover:bg-surface transition-colors">
                    <td className="px-[18px] py-[10px]">
                      {u.name && <p className="font-medium text-[13px] text-[#111827]">{u.name}</p>}
                      <p className={u.name ? 'text-[12px] text-[#9CA3AF]' : 'text-[13px] text-[#111827]'}>{u.email || '—'}</p>
                    </td>
                    <td className="px-[18px] py-[10px]">
                      {changingRoleFor === u.id ? (
                        <div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
                            {ALL_ROLES.map((r) => (
                              <label key={r} className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={roleChangeValues.includes(r)}
                                  onChange={() => toggleRole(r)}
                                  className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                                />
                                <span className="text-[12px] text-[#374151]">{ROLE_LABELS[r]}</span>
                              </label>
                            ))}
                          </div>
                          {roleChangeValues.length === 0 && (
                            <p className="text-[11px] text-dr mb-2">At least one role is required.</p>
                          )}
                          {carriesPortals(roleChangeValues) && (
                            <div className="mb-3 pt-3 border-t border-[#F3F4F6]">
                              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.6px] mb-1.5">Portal access</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                                {PORTAL_OPTIONS.map((p) => (
                                  <label key={p.key} className="flex items-center gap-1.5 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={portalChangeValues.includes(p.key)}
                                      onChange={() => togglePortalChange(p.key)}
                                      className="rounded border-[#D1D5DB] text-pb focus:ring-pb"
                                    />
                                    <span className="text-[12px] text-[#374151]">{p.label}</span>
                                  </label>
                                ))}
                              </div>
                              {portalChangeValues.length === 0 && (
                                <p className="text-[11px] text-dr mt-1.5">Select at least one portal.</p>
                              )}
                            </div>
                          )}
                          {roleChangeValues.includes('football_ops') && (
                            <div className="mb-3 pt-3 border-t border-[#F3F4F6]">
                              <FootballOpsAccessEditor value={foPermsChangeValues} onChange={setFoPermsChangeValues} />
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => saveRoleChange(u.id)}
                              disabled={roleChangeSaving || roleChangeValues.length === 0 || (carriesPortals(roleChangeValues) && portalChangeValues.length === 0)}
                              className="text-[12px] text-white bg-pb hover:bg-navy px-2 py-1 rounded-[6px] transition disabled:opacity-60"
                            >
                              {roleChangeSaving ? '…' : 'Save'}
                            </button>
                            <button
                              onClick={() => setChangingRoleFor(null)}
                              className="text-[12px] text-[#9CA3AF] hover:text-[#6B7280] transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1 items-center">
                          {roles.length > 0 ? roles.map((r) => (
                            <span
                              key={r}
                              className={`text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] ${ROLE_STYLES[r] || 'bg-[#F3F4F6] text-[#6B7280]'}`}
                            >
                              {ROLE_LABELS[r] ?? r}
                            </span>
                          )) : (
                            <span className="text-[12px] text-[#9CA3AF]">—</span>
                          )}
                          {roles.includes('football_ops') && isFootballOpsLimited(u) && (
                            <span className="text-[10.5px] font-semibold px-[8px] py-[3px] rounded-[12px] bg-amber-light text-[#92400E]" title="Some Football Ops areas are switched off — see Manage roles">
                              Limited access
                            </span>
                          )}
                          {carriesPortals(roles) && getUserPortals(u).map((p) => (
                            <span key={p} className="text-[10.5px] font-medium px-[8px] py-[3px] rounded-[12px] bg-[#F3F4F6] text-[#6B7280]">
                              {PORTAL_LABELS[p] || p}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-[18px] py-[10px] text-[#9CA3AF] text-[12px]">{u.createdByEmail || '—'}</td>
                    <td className="px-[18px] py-[10px] text-[#9CA3AF] text-[12px]">{formatDate(u.createdAt)}</td>
                    {isAdmin && (
                      <td className="px-[18px] py-[10px]">
                        <div className="flex items-center gap-3">
                          {changingRoleFor !== u.id && u.id !== currentUser?.uid && (
                            <button
                              onClick={() => startRoleChange(u)}
                              className="text-[12px] text-pb hover:text-navy font-medium transition"
                            >
                              Manage roles
                            </button>
                          )}
                          {u.id !== currentUser?.uid && (
                            <button
                              onClick={() => resendInvite(u)}
                              disabled={resendingInviteFor === u.id}
                              className="text-[12px] text-[#9CA3AF] hover:text-[#6B7280] font-medium disabled:opacity-60 transition"
                              title="Resend password setup email"
                            >
                              {resendingInviteFor === u.id ? 'Sending…' : 'Resend invite'}
                            </button>
                          )}
                          {u.id !== currentUser?.uid && (
                            <button
                              onClick={() => setConfirmDeleteUser({ id: u.id, email: u.email || u.id })}
                              className="text-[12px] text-dr hover:text-[#7F1D1D] font-medium transition"
                            >
                              Delete
                            </button>
                          )}
                          {u.id === currentUser?.uid && (
                            <span className="text-[12px] text-[#9CA3AF] italic">You</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
